import React, { useState, useEffect, useContext } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import LoginApp from './components/Login/Login';
import Dashboard from './components/Dashboard/UserDashboard/UserDashboard';
import AdminDashboard from './components/Dashboard/AdminDashboard/AdminDashboard';
import UserDetail from './components/Dashboard/AdminDashboard/UserDetail/UserDetail';
import UserPerformance from './components/Dashboard/AdminDashboard/UserPerformance';
import PrivateRoute from './components/PrivateRoute';
import { NotificationProvider } from './components/Notification/NotificationContext';
import { useNotifications } from './components/Notification/NotificationContext';
import Header from './components/HomePage/Header/Header';
import HeroSection from './components/HomePage/HeroSection/HeroSection';
import Services from './components/HomePage/Services/Services';
import Body from './components/HomePage/Body/Body';
import AboutMe from './components/HomePage/AboutMe/AboutMe';
import Footer from './components/HomePage/Footer/Footer';
import NavBar from './components/Dashboard/NavBar';
import { checkSurveyCompletion } from './services/surveyService';
import { getUserProfile } from './services/userService';
import { checkTokenValidity } from './services/authService';
import { UserContext } from './contexts/UserContext';
import { Toolbar } from '@mui/material';
import { setNavigate, setModalHandler, setUserHandler, handleSessionExpired } from './components/SessionExpiredHandler';
import { getDailyQuestionResponses } from './services/dailyQuestionService';
import ForgotPassword from './components/Login/ForgotPassword/ForgotPassword';
import ResetPassword from './components/Login/ResetPassword/ResetPassword';
import SessionExpiredModal from './components/SessionExpiredModal';
import { TourProvider } from './components/UserGuide/TourProvider';
import TourComponent from './components/UserGuide/TourComponent';
import WelcomeAfterRegistration from './components/Login/WelcomeAfterRegistration';
import ControlGroupWelcome from './components/Login/ControlGroupWelcome';
import ExperimentalGroupWelcome from './components/Login/ExperimentalGroupWelcome';
import CountdownPage from './components/ControlGroup/CountdownPage';


const AppContent = ({ content, setContent }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [surveyCompleted, setSurveyCompleted] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [loggedIn, setLoggedIn] = useState(false);
    const [isDailySurveyDone, setIsDailySurveyDone] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, setUser, login } = useContext(UserContext);
    const { checkAndAddNotification, markDailySurveyAsDone } = useNotifications();
    console.log(user)
    console.log(content)

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error('No token found in localStorage');
                }

                const userProfile = await getUserProfile(token);

                const { firstname, email, username, avatar, avatarContentType, _id, isAdmin, place, street, telephone, lastname, hasSeenTour, studyGroup, createdAt } = userProfile;
                const completeUser = { firstname, email, username, avatar, avatarContentType, token, _id, isAdmin, place, street, telephone, lastname, hasSeenTour, studyGroup, createdAt };
                setUser(completeUser);

                if (!isAdmin) {
                    const isSurveyCompleted = await checkSurveyCompletion(_id);
                    setSurveyCompleted(isSurveyCompleted);

                    // Check if user was created before October 1, 2025
                    const cutoffDate = new Date('2025-10-01');
                    const userCreatedAt = new Date(createdAt);
                    const isLegacyUser = userCreatedAt < cutoffDate;

                    if (isLegacyUser) {
                        console.log('Legacy user detected (created before Oct 1, 2025), allowing dashboard access');
                        if (!isSurveyCompleted) {
                            setContent('surveyIntroduction');
                        } else {
                            setContent(location.state?.initialContent || 'overview');
                        }
                    } else if (studyGroup === 'control') {
                        console.log('Control group user detected. Survey completed:', isSurveyCompleted);
                        if (!isSurveyCompleted) {
                            // Control group hasn't completed survey
                            const hasSeenWelcome = sessionStorage.getItem('controlGroupWelcomeSeen') === 'true';

                            if (location.pathname === '/dashboard' && hasSeenWelcome) {
                                // They've seen welcome and are on dashboard - let them continue with survey
                                console.log('Control group has seen welcome, continuing with survey');
                                setContent('surveyIntroduction');
                            } else if (location.pathname !== '/control-welcome' && !hasSeenWelcome) {
                                // They haven't seen welcome yet - redirect to control welcome
                                console.log('Redirecting control group user to welcome page');
                                navigate('/control-welcome', { replace: true });
                            } else if (location.pathname === '/control-welcome') {
                                // They're on the welcome page - do nothing
                                console.log('Control group user on welcome page');
                            }
                        } else {
                            // Control group completed survey
                            console.log('Control group has completed survey');
                            // Check if user is coming from countdown page to view results
                            if (location.pathname === '/dashboard' && location.state?.showResults) {
                                // User clicked "View Results" from countdown page
                                console.log('Control group user viewing results');
                                setContent('result');
                            } else if (location.pathname === '/dashboard' && !location.state?.showResults) {
                                // They're on dashboard but didn't come from countdown with showResults
                                console.log('Redirecting control group to countdown');
                                navigate('/countdown', { replace: true });
                            } else if (location.pathname === '/countdown') {
                                // They're on the countdown page - do nothing
                                console.log('Control group user on countdown page');
                            }
                        }
                    } else if (studyGroup === 'experimental') {
                        // Experimental group - show welcome page first, then survey
                        console.log('Experimental group user detected. Survey completed:', isSurveyCompleted);
                        if (!isSurveyCompleted) {
                            // Experimental group hasn't completed survey
                            const hasSeenWelcome = sessionStorage.getItem('experimentalGroupWelcomeSeen') === 'true';

                            if (location.pathname === '/dashboard' && hasSeenWelcome) {
                                // They've seen welcome and are on dashboard - let them continue with survey
                                console.log('Experimental group has seen welcome, continuing with survey');
                                setContent('surveyIntroduction');
                            } else if (location.pathname !== '/experimental-welcome' && !hasSeenWelcome) {
                                // They haven't seen welcome yet - redirect to experimental welcome
                                console.log('Redirecting experimental group user to welcome page');
                                navigate('/experimental-welcome', { replace: true });
                            } else if (location.pathname === '/experimental-welcome') {
                                // They're on the welcome page - do nothing
                                console.log('Experimental group user on welcome page');
                            }
                        } else {
                            setContent(location.state?.initialContent || 'overview');
                        }
                    } else if (!studyGroup || studyGroup === 'unassigned' || studyGroup === '') {
                        // Unassigned users should see welcome page
                        console.log('Unassigned user detected, redirecting to welcome page');
                        navigate('/welcome', { replace: true });
                    }

                    // Check for daily question responses (skip for control group but include legacy users)
                    if (studyGroup !== 'control' || isLegacyUser) {
                        try {
                            const dailyResponses = await getDailyQuestionResponses(_id);
                            const today = new Date().toISOString().split('T')[0];
                            const responses = Object.values(dailyResponses.data);

                            const hasTodayResponse = responses.some(response => {
                                const responseDate = new Date(response.date).toISOString().split('T')[0];
                                return responseDate === today;
                            });

                            if (!isSurveyCompleted && responses.length === 0) {
                                console.log('Survey not completed and no daily responses found. Skipping notification.');
                            } else if (!hasTodayResponse) {
                                checkAndAddNotification(); // Trigger notification handling in context
                            } else {
                                setIsDailySurveyDone(true);
                                markDailySurveyAsDone();
                            }
                        } catch (error) {
                            console.error('Error fetching daily question responses:', error);
                        }
                    }
                } else {
                    setContent(location.state?.initialContent || 'personalInfo');
                }
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    handleSessionExpired();
                    setModalOpen(true); // Show the session expired modal
                } else {
                    console.error("Error fetching initial data:", error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [loggedIn, setUser, setContent, checkAndAddNotification, markDailySurveyAsDone, location, navigate]);


    // Separate effect to handle navigation state for control group results viewing
    useEffect(() => {
        if (user && user.studyGroup === 'control' && surveyCompleted &&
            location.pathname === '/dashboard' && location.state?.showResults) {
            console.log('Control group user clicked view results - setting content to result');
            setContent('result');
        }
    }, [location.pathname, location.state, user, surveyCompleted, setContent]);

    useEffect(() => {
        setNavigate(navigate);
        setModalHandler(setModalOpen);
        setUserHandler(setUser);
    }, [navigate, setUser]);

    useEffect(() => {
        const intervalId = setInterval(async () => {
            if (user) {
                const isValid = await checkTokenValidity();
                if (!isValid) {
                    clearInterval(intervalId); // Stop polling if the token is invalid
                }
            }
        }, 10800000); // 3 hours

        return () => clearInterval(intervalId); // Cleanup interval on component unmount
    }, [user]);

    const onAnswerQuestion = (notification) => {
        console.log(notification.type);
        if (location.pathname !== '/dashboard') {
            navigate('/dashboard', { replace: true });
            // Use a slight delay to ensure navigation is complete before setting content
            setTimeout(() => {
                if (notification.type === 'hourly') {
                    setContent('performanceQuestion');
                } else if (notification.type === 'daily') {
                    setContent('DailySurvey');
                }
            }, 100);
        } else {
            if (notification.type === 'hourly') {
                setContent('performanceQuestion');
            } else if (notification.type === 'daily') {
                setContent('DailySurvey');
            }
        }
    };

    const handleMenuItemClick = (item) => {
        setContent(item);
        setDrawerOpen(false);
    };

    const handleSurveyCompletion = () => {
        setSurveyCompleted(true);

        // Clear the welcome seen flags when survey is completed
        sessionStorage.removeItem('controlGroupWelcomeSeen');
        sessionStorage.removeItem('experimentalGroupWelcomeSeen');

        // Check if user is in control group
        if (user && user.studyGroup === 'control') {
            // Navigate to countdown page for control group
            navigate('/countdown');
        } else {
            // Regular flow for experimental group
            markDailySurveyAsDone();
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <TourProvider>
        <NotificationProvider user={user} navigate={navigate} onAnswerQuestion={onAnswerQuestion} setContent={setContent} content={content} >
            <div className="App">
                {user ? (
                    <>
                        <NavBar
                            setDrawerOpen={setDrawerOpen}
                            drawerOpen={drawerOpen}
                            handleMenuItemClick={handleMenuItemClick}
                            surveyCompleted={surveyCompleted}
                        />
                        <Toolbar />
                    </>
                ) : (
                    <Header />
                )}
                <Routes>
                    <Route path="/" element={<><HeroSection /><Services /><Body /><AboutMe /></>} />
                    <Route path="/login" element={<LoginApp setContent={setContent} setLoggedIn={setLoggedIn} />} />
                    <Route path="/welcome" element={<PrivateRoute><WelcomeAfterRegistration /></PrivateRoute>} />
                    <Route path="/control-welcome" element={<PrivateRoute><ControlGroupWelcome setContent={setContent} /></PrivateRoute>} />
                    <Route path="/experimental-welcome" element={<PrivateRoute><ExperimentalGroupWelcome setContent={setContent} /></PrivateRoute>} />
                    <Route path="/countdown" element={<PrivateRoute><CountdownPage /></PrivateRoute>} />
                    <Route path="/dashboard" element={<PrivateRoute><Dashboard content={content} setContent={setContent} onAnswerQuestion={onAnswerQuestion} onSurveyCompletion={handleSurveyCompletion} /></PrivateRoute>} />
                    <Route path="/admin-dashboard" element={<PrivateRoute adminOnly={true}><AdminDashboard content={content} setContent={setContent} /></PrivateRoute>} />
                    <Route path="/admin/user/:userId" element={<PrivateRoute adminOnly={true}><UserDetail /></PrivateRoute>} />
                    <Route path="/admin/user/:userId/performance" element={<PrivateRoute adminOnly={true}><UserPerformance /></PrivateRoute>} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                </Routes>
                <Footer />
                <SessionExpiredModal open={modalOpen} handleClose={() => setModalOpen(false)} />
                {user && (
                    <TourComponent 
                        surveyCompleted={surveyCompleted} 
                        drawerOpen={drawerOpen}
                        setDrawerOpen={setDrawerOpen}
                    />
                )}
            </div>
        </NotificationProvider>
        </TourProvider>
    );
};

export default AppContent;
